import {dehydrate, QueryClient} from '@tanstack/react-query';
import {GetStaticPropsContext} from 'next/types';

import {
    Academy,
    getAllAcademiesForLocaleFromService,
    InDemandCareer,
    buildInDemandCareer,
    getGwgAccelerator,
} from '@udemy/career-academies';
import {RENDER_MODES} from '@udemy/nextjs-core';
import {udSentry} from '@udemy/sentry';

import {LoggedOutHomePageFeature} from 'src/features/lohp';
import {
    DynamicWebContentPageType,
    useCategoryUniqueLearnerStatsQuery,
    useMarketingSplitCardsCarouselV1Query,
    useDynamicWebContentTestimonialsQuery,
    useDynamicWebContentQuery,
} from 'src/generated/graphql';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {FOUR_HOURS} from 'src/lib/constants';
import {fetcher} from 'src/lib/gql-fetcher';
import {AppContextData} from 'src/types/page-props';

import {getStaticAppContext} from '../api/context.api';
import {getAcademyDetailsData} from './lib/get-academy-details-data';

/** Shape expected by @udemy/react-globalization-components category modules. */
export interface CategoryUniqueLearnerStat {
    categoryId: number;
    uniqueLearnerCount: number;
}

export type CategoryUniqueLearnerStats = CategoryUniqueLearnerStat[];

export type CategoryUniqueLearnerStatsMap = Record<string, CategoryUniqueLearnerStats>;

const CATEGORY_STATS_COUNTRIES = ['JP'];

async function fetchCategoryUniqueLearnerStatsForCountry(
    countryCode: string,
): Promise<CategoryUniqueLearnerStats | null> {
    try {
        const data = await useCategoryUniqueLearnerStatsQuery.fetcher({countryCode})();
        const raw = data?.categoryUniqueLearnerStats;
        if (!raw?.length) return null;
        return raw.map((stat) => ({
            categoryId: Number(stat.courseCategory.id),
            uniqueLearnerCount: stat.uniqueLearnerCount,
        }));
    } catch (error) {
        udSentry.captureException(error);
        return null;
    }
}

async function fetchCategoryUniqueLearnerStatsMap(): Promise<CategoryUniqueLearnerStatsMap> {
    const results = await Promise.allSettled(
        CATEGORY_STATS_COUNTRIES.map(async (code) => ({
            code,
            stats: await fetchCategoryUniqueLearnerStatsForCountry(code),
        })),
    );

    const map: CategoryUniqueLearnerStatsMap = {};
    for (const result of results) {
        if (result.status === 'fulfilled' && result.value.stats) {
            map[result.value.code] = result.value.stats;
        }
    }
    return map;
}

const _getRevalidateTime = () => {
    // We expect the getGwgAccelerator call to fail during CI, so set the invalidation time to 1 second.
    // This way, the cache is invalidated and the page is immediately rerendered once it deploys.
    if (process.env.IS_CI) {
        return 1;
    }

    return FOUR_HOURS;
};

/**
 * @remarks
 * Normally, we prefer to use just ISR without any pre-generation on the server. However, in this case, we
 * needed to remove that dynamic routing since it was causing path mismatch on the client side hydration such as:
 * Requested URL (href) on client -> /
 * Page Path on server (next.js)  -> /lohp/
 *
 * This causes mismatch on next.js hydration since it expected /lohp on the client side href.
 */
export const getStaticProps = async ({locale}: GetStaticPropsContext) => {
    locale = locale as string;
    const translations = await getCompactTranslations(locale as string);
    const client = new QueryClient();

    // Resolve appContext
    const staticDataPromise = getStaticAppContext({locale});

    // Resolve Career Academies
    let academiesListForLocale: Academy[] = [];
    try {
        const result = await getAllAcademiesForLocaleFromService(locale, {
            customGqlFetcher: fetcher,
        });
        academiesListForLocale = (result ?? []).filter((a): a is Academy => a !== undefined);
    } catch (error) {
        udSentry.captureException(error);
    }

    const academyDetailsPromise = Promise.allSettled(
        academiesListForLocale.map((academy) =>
            getAcademyDetailsData(academy.curriculum.buyableIds.map(Number), locale),
        ),
    );

    /**
     * NOTE:
     * Normally, we configure ISR pages as dynamic routes so we can defer the initial render until after the app is deployed.
     * This allows us to sidestep issues like not being able to call certain GQL APIs from the github runners
     * due to the runners missing certain GQL permissions.
     *
     * We can't do this for the LOHP (see this PR: https://github.com/udemy/frontends-homepage/pull/278 for context),
     * so certain GQL calls (like the one below for GWG accelerators) just fail.
     * We're currently swallowing the failure so CI can pass and the deploy isn't blocked; once the app is deployed,
     * the GQL call will succeed when the render is run on a service host with actual GQL permissions.
     *
     * This is undesirable because it means that the first iteration of the cached page will not have any gwg accelerator data
     * (in more practical terms, for four hours after a deploy, the gwg accelerator carousel will not show up on the homepage).
     *
     * To compensate for this, we have implemented a workaround that sets the invalidation time for an LOHP generated during CI
     * to 1 second (see the _getRevalidateTime function for more details). So once the app is deployed,
     * the cache will be invalidated almost immediately, and the page will rerender with the gwg accelerator carousel.
     */
    const HARD_CODED_ACCELERATOR_SLUG = 'ai-professional-certificate-learning-path';
    let acceleratorData = null;
    try {
        acceleratorData = await (
            getGwgAccelerator as (
                path: string,
                locale: string,
                options?: {customGqlFetcher?: typeof fetcher},
            ) => ReturnType<typeof getGwgAccelerator>
        )(HARD_CODED_ACCELERATOR_SLUG, locale, {
            customGqlFetcher: fetcher,
        });
    } catch (error) {
        udSentry.captureException(error);
    }

    // Prefetch CMS-backed content into the QueryClient so it's included in the
    // dehydrated state and rendered server-side (visible to Googlebot/AI crawlers).
    const cmsPageType = DynamicWebContentPageType.LoggedOutHomePage;
    const partnersVariables = {pageType: cmsPageType, isContentPreviewEnabled: false};
    const testimonialsVariables = {pageType: cmsPageType};

    const cmsPrefetchPromise = Promise.all([
        client.prefetchQuery(
            useMarketingSplitCardsCarouselV1Query.getKey(),
            useMarketingSplitCardsCarouselV1Query.fetcher(),
        ),
        client.prefetchQuery(
            useDynamicWebContentTestimonialsQuery.getKey(testimonialsVariables),
            useDynamicWebContentTestimonialsQuery.fetcher(testimonialsVariables),
        ),
        client.prefetchQuery(
            useDynamicWebContentQuery.getKey(partnersVariables),
            useDynamicWebContentQuery.fetcher(partnersVariables),
        ),
    ]);

    const categoryStatsMapPromise = fetchCategoryUniqueLearnerStatsMap();

    const [staticData, academyDetailsResults, , categoryUniqueLearnerStatsMap] = await Promise.all([
        staticDataPromise,
        academyDetailsPromise,
        cmsPrefetchPromise,
        categoryStatsMapPromise,
    ]);

    const appContext: AppContextData = staticData;

    const academiesList = academyDetailsResults.reduce<InDemandCareer[]>(
        (acc, detailsResult, index) => {
            if (detailsResult.status === 'fulfilled') {
                const academy = academiesListForLocale[index];
                const courses = detailsResult.value;
                acc.push(buildInDemandCareer(academy, courses));
            } else {
                udSentry.captureException(detailsResult.reason);
            }
            return acc;
        },
        [],
    );

    return {
        props: {
            appContext,
            dehydratedState: dehydrate(client),
            locale,
            pageKey: 'discovery_logged_out_home',
            renderMode: RENDER_MODES.ISR, // supports render functions in _document.tsx and _app.tsx.
            translations,
            academies: academiesList,
            accelerator: acceleratorData || null,
            categoryUniqueLearnerStatsMap,
        },
        revalidate: _getRevalidateTime(),
    };
};

export default LoggedOutHomePageFeature;
